/**
 * Tenant display currency — resolve + subscribe.
 *
 * Multi-tenant: the currency shown everywhere in V3 is the signed-in client's own
 * (configured in General Settings → Countries as `country_currency`), not a global
 * 'AED'. The actual value lives in a module-level singleton in Shared/Formatters.js
 * (getActiveCurrency/setActiveCurrency) so all formatMoney() call sites read it
 * without prop-threading. This file resolves that value from the API and keeps it
 * fresh:
 *
 *   - resolveAndApplyCurrency() : fetch the client's countries, pick a currency,
 *                                 push it into the singleton + localStorage cache,
 *                                 and notify subscribers.
 *   - CurrencyBoot              : mount once at app root; resolves on boot and when
 *                                 the active concessionaire changes (mall switch).
 *   - useCurrency()             : live-updating hook for the few spots that must
 *                                 re-render when the currency changes.
 */
import { useEffect, useState } from 'react';
import { v3Api } from './useApi';
import { getActiveCurrency, setActiveCurrency } from '../Shared/Formatters';

const STORAGE_KEY = 'displayCurrency';
const STORAGE_CID_KEY = 'displayCurrencyCid';
const CHANGE_EVENT = 'v3:currencychange';
const DEFAULT_CURRENCY = 'AED';

// De-dupe concurrent resolves (e.g. CurrencyBoot mount racing a storage event).
let inFlightCid = null;

/**
 * Pick the client's display currency from its country list.
 * One-per-client: first country with a non-empty currency wins. (If a client ever
 * operates in multiple differing-currency countries, per-property scope would be
 * the follow-up; today they share one code.)
 */
function pickCurrency(countries) {
    if (!Array.isArray(countries)) return null;
    for (const c of countries) {
        const code = c && typeof c.country_currency === 'string' ? c.country_currency.trim() : '';
        if (code) return code;
    }
    return null;
}

/**
 * Push a resolved currency into the singleton + cache and notify subscribers.
 * Keyed by concessionaire so a mall switch re-resolves rather than reusing a stale
 * cached code.
 */
export function applyDisplayCurrency(code, concessionaireId) {
    const next = (code && code.trim()) || DEFAULT_CURRENCY;
    setActiveCurrency(next);
    try {
        localStorage.setItem(STORAGE_KEY, next);
        if (concessionaireId) localStorage.setItem(STORAGE_CID_KEY, String(concessionaireId));
    } catch { /* quota / private mode — non-fatal */ }
    try {
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
    } catch { /* CustomEvent unsupported — non-fatal */ }
    return next;
}

/**
 * Resolve the display currency for a concessionaire from the API and apply it.
 * No-op (returns the current value) when there's no concessionaire id.
 * Safe to call redundantly — failures leave the current/cached currency intact.
 */
export async function resolveAndApplyCurrency(concessionaireId) {
    if (!concessionaireId) return getActiveCurrency();
    if (inFlightCid === concessionaireId) return getActiveCurrency();
    inFlightCid = concessionaireId;
    try {
        const countries = await v3Api.country.listByConcessionaire(concessionaireId);
        const code = pickCurrency(countries);
        if (code) return applyDisplayCurrency(code, concessionaireId);
        // No currency configured — stamp the cid so we don't refetch every boot,
        // but leave the currency at its current (seeded/default) value.
        try { localStorage.setItem(STORAGE_CID_KEY, String(concessionaireId)); } catch { /* non-fatal */ }
        return getActiveCurrency();
    } catch {
        return getActiveCurrency();
    } finally {
        inFlightCid = null;
    }
}

/**
 * Null-render resolver. Mount ONCE at app root. Resolves on boot and whenever the
 * active concessionaire changes; skips the network round-trip when the cache
 * already matches the current concessionaire.
 */
export function CurrencyBoot() {
    useEffect(() => {
        const sync = () => {
            let cid = '';
            let cachedCid = '';
            try {
                cid = localStorage.getItem('concessionaireId') || '';
                cachedCid = localStorage.getItem(STORAGE_CID_KEY) || '';
            } catch { /* localStorage unavailable */ }
            if (!cid) return;
            if (cid === cachedCid) return; // cache already matches this client
            resolveAndApplyCurrency(cid);
        };
        sync();
        // A login / mall switch in another tab (or this one) updates localStorage.
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, []);
    return null;
}

/**
 * Live currency for components that must re-render on change (the few money-render
 * spots that bypass formatMoney's default). Most code can call getActiveCurrency()
 * directly since the singleton is seeded synchronously at module load.
 */
export function useCurrency() {
    const [currency, setCurrency] = useState(getActiveCurrency);
    useEffect(() => {
        const onChange = () => setCurrency(getActiveCurrency());
        const onStorage = (e) => { if (e.key === STORAGE_KEY) setCurrency(getActiveCurrency()); };
        window.addEventListener(CHANGE_EVENT, onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(CHANGE_EVENT, onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);
    return currency;
}
